/**
 * Theme Core
 *
 * Sets up component, section and block initialization on the front-end and in the admin.
 *
 * Do not edit this file.
 * Do not import this file. An instance of this file is already available in the global scope.
 *
 * @package Theme
 * @returns {Readonly<{init, component, setup, block, section}>}
 */
function buildThemeCore() {
    const _themeEntities = {
        components: {},
        blocks: {},
        sections: {},
    };

    const _themePackages = {};

    return Object.freeze({
        component,
        block,
        section,
        init,
        setup,
        packages: _themePackages,
        registerPackages,
    });

    /**
     * Register a component.
     *
     * @param {String} name
     * @param {Function} callback
     */
    function component(name, callback) {
        _themeEntities.components[name] = callback;
    }

    /**
     * Register a block.
     *
     * @param {String} name
     * @param {Function} callback
     */
    function block(name, callback) {
        _themeEntities.blocks[name] = callback;
    }

    /**
     * Register a section.
     *
     * @param {String} name
     * @param {Function} callback
     */
    function section(name, callback) {
        _themeEntities.sections[name] = callback;
    }

    /**
     * Get the data from an element and clean up attributes.
     *
     * @param {HTMLElement} element
     * @param {String} type
     *
     * @returns {any}
     * @private
     */
    function _getFieldsAndCleanUp(element, type) {
        let data = null;

        if (typeof element.dataset.fields !== 'undefined') {
            // Convert from Base64 to JSON.
            data = JSON.parse(atob(element.dataset.fields));
        }

        // Remove attributes to prevent the component from being initialized twice.
        element.removeAttribute(`data-${type}`);

        if (element.hasAttribute('data-fields')) {
            element.removeAttribute('data-fields');
        }

        return data;
    }

    /**
     * Initialize a block.
     *
     * @param {String} name
     * @param {String} id
     * @param {Object|null} data
     *
     * @returns {Element|null}
     * @private
     */
    function _initBlockById(name, id, data = null) {
        // Attempt to find the HTML instance of the block.
        const instance = document.querySelector(`[data-block="${name}"][data-block-id="${id}"]`);
        if (!instance) return null;

        // Run the block's callback, if there is one.
        const callback = _themeEntities.blocks[name];
        if (callback) callback(instance, data);

        return instance;
    }

    /**
     * Initialize all blocks on the frontend.
     *
     * @private
     */
    function _initFrontendBlocks() {
        for (const [name, callback] of Object.entries(_themeEntities.blocks)) {
            const instances = document.querySelectorAll(`[data-block="${name}"]`);
            instances.forEach(i => {
                const blockId = i.dataset.blockId || '';
                const data = blockId && typeof window[blockId] !== 'undefined' ? window[blockId].data : null;

                if (callback) callback(i, data);
            });
        }
    }

    /**
     * Initialize all registered components.
     *
     * @param {Element|null} scope
     *
     * @private
     */
    function _initComponents(scope = null) {
        if (!scope) scope = document;

        // Attempt to find the HTML instances of the component.
        for (const [name, callback] of Object.entries(_themeEntities.components)) {
            const instances = scope.querySelectorAll(`[data-component="${name}"]`);
            instances.forEach(i => callback(i, _getFieldsAndCleanUp(i, 'component')));
        }
    }

    /**
     * Initialize all registered sections.
     *
     * @param {Element|null} scope
     *
     * @private
     */
    function _initSections(scope = null) {
        if (!scope) scope = document;

        // Attempt to find the HTML instances of the section.
        for (const [name, callback] of Object.entries(_themeEntities.sections)) {
            const instances = scope.querySelectorAll(`[data-section="${name}"]`);
            instances.forEach(i => callback(i, _getFieldsAndCleanUp(i, 'section')));
        }
    }

    /**
     * Initialize all registered components and blocks.
     */
    function init() {
        _initFrontendBlocks();
        _initComponents();
        _initSections();
    }

    /**
     * Register global third-party packages to be used in any file.
     *
     * @param packages
     */
    function registerPackages(packages) {
        for (const key in packages) {
            _themePackages[key] = packages[key];
        }
    }

    /**
     * Set up the ThemeCore object.
     */
    function setup() {
        if (typeof window.initializer !== 'undefined' && window.initializer.isAdmin) {
            window.initializer.onInitBlock((name, id, data = null) => {
                const blockElement = _initBlockById(name, id, data);
                if (blockElement) _initComponents(blockElement);
            });
        } else {
            window.addEventListener('DOMContentLoaded', init);
        }
    }
}

window.theme = buildThemeCore();
window.theme.setup();

export default window.theme;
